import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactElement,
  RefAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useAssetUrl } from '../lib/hooks';
import { Typography } from '../typography';

import styles from './modal.scss';

type Props = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    openModal: boolean;
    desktopModalWidth?: number;
    image?: string;
    hero?: boolean;
    title?: string;
    description?: string;
    vertical?: boolean;
    children?: ReadonlyArray<ReactElement>;
    buttonNumber?: number;
    shadowClick?: boolean;
    minDesktopModalHeight?: number;
    toggleModal?: () => void;
    buttons?: React.ReactNode;
    header?: React.ReactNode;
    dataTestId?: string;
  };

const MIN_DESKTOP_MODAL_WIDTH = 474;
const MIN_DESKTOP_MODAL_HEIGHT = 404;

const COMPONENT_ID = 'modulor-modal';

export const Modal: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      openModal,
      image,
      hero,
      vertical,
      header,
      title,
      buttons,
      buttonNumber,
      description,
      toggleModal,
      shadowClick,
      desktopModalWidth = MIN_DESKTOP_MODAL_WIDTH,
      minDesktopModalHeight = MIN_DESKTOP_MODAL_HEIGHT,
      dataTestId = COMPONENT_ID,
      className,
      ...rest
    },
    ref,
  ) => {
    const [hasScroll, setHasScroll] = useState<boolean>(false);
    const modalWrapper = useRef<HTMLDivElement>(null);
    const content = useRef<HTMLDivElement>(null);
    const container = useRef<HTMLDivElement>(null);
    const footer = useRef<HTMLDivElement>(null);

    const getFooterHeights = useCallback(() => {
      const footerHeight = buttonNumber ? footer?.current.clientHeight : 16;

      return {
        footerHeight,
      };
    }, [buttonNumber]);

    const setHeight = useCallback(() => {
      if (typeof window === 'undefined') return;

      const { footerHeight } = getFooterHeights();
      const maxPossibleHeight = window.innerHeight - 32;

      container.current.style.maxHeight = `${maxPossibleHeight}px`;
      modalWrapper.current.style.maxHeight = `${maxPossibleHeight}px`;
      content.current.style.paddingBottom = `${footerHeight}px`;
      modalWrapper.current.classList.add('expanded');
    }, [getFooterHeights]);

    const setDesktopMaxHeight = useCallback(() => {
      const { footerHeight } = getFooterHeights();

      const maxModalHeight = parseInt(getComputedStyle(modalWrapper.current).maxHeight, 10);
      const maxContentHeight = maxModalHeight - footerHeight - 32;
      content.current.style.maxHeight = `${maxContentHeight}px`;
    }, [getFooterHeights]);

    const setDesktopModalMinHeight = useCallback(() => {
      if (minDesktopModalHeight <= 0) return;
      modalWrapper.current.style.minHeight = `${minDesktopModalHeight}px`;
    }, [minDesktopModalHeight]);

    useEffect(() => {
      if (!openModal) return;

      if (openModal && window.innerWidth <= 1023) {
        setHeight();
      } else if (openModal) {
        setDesktopMaxHeight();
        setDesktopModalMinHeight();
      }

      if (content.current.scrollHeight > content.current.clientHeight) {
        setHasScroll(true);
      } else {
        setHasScroll(false);
      }
    }, [
      setHeight,
      openModal,
      setDesktopModalMinHeight,
      setDesktopMaxHeight,
      children,
      header,
      buttons,
      title,
      description,
      image,
      hero,
      vertical,
    ]);

    const imageUrl = useAssetUrl(image);

    const headerBackground = imageUrl && hero ? { backgroundImage: `url('${imageUrl}')` } : {};
    const modalWidth = window?.innerWidth <= 1023 ? '100%' : desktopModalWidth + 'px';

    return openModal ? (
      <div
        ref={ref}
        data-id={dataTestId}
        data-test-id={dataTestId}
        data-component-name={COMPONENT_ID}
        className={classNames(className)}
        {...rest}>
        <div
          style={{
            width: modalWidth,
          }}
          ref={modalWrapper}
          className={classNames(styles['modal-wrapper'], {
            [styles.open]: openModal,
            [styles.desktopModalWidth]: desktopModalWidth,
          })}>
          <div className={styles['container']} ref={container}>
            <div
              data-testid={`${dataTestId}-header`}
              className={classNames(styles['header'], {
                [styles['with-image']]: image,
                [styles.hero]: image && hero,
                [styles.hidden]: header === null,
              })}
              style={headerBackground}>
              {header}
              {image && !hero ? (
                <div
                  className={styles.image}
                  data-testid={`${dataTestId}-image`}
                  style={{ backgroundImage: `url('${imageUrl}')` }}
                />
              ) : null}
            </div>
            <div ref={content} data-testid={`${dataTestId}-content`} className={styles['content']}>
              {title && (
                <Typography
                  className={styles['title']}
                  dataTestId={`${dataTestId}-title`}
                  variant="large-title-semibold">
                  {title}
                </Typography>
              )}
              {description && (
                <Typography
                  className={styles['description']}
                  dataTestId={`${dataTestId}-description`}
                  variant="body-regular">
                  {description}
                </Typography>
              )}
              {children}
            </div>
          </div>
          <div
            ref={footer}
            data-testid={`${dataTestId}-buttons`}
            className={classNames({
              [styles['button-docked']]: buttonNumber,
              [styles.vertical]: vertical,
              [styles['has-border']]: hasScroll,
            })}>
            {buttons}
          </div>
        </div>
        <div
          className={styles['shadow']}
          data-testid={`${dataTestId}-shadow`}
          onClick={shadowClick ? toggleModal : null}></div>
      </div>
    ) : null;
  },
);

Modal.displayName = 'Modal';
